// Pallets on a delivery (spec #4006, mig 20261211000000).
//
// A truck stops with more crates than one pallet holds. Where the business
// weighs at intake (tenants.capabilities.intake_weight), each variety line of
// the intake form lists its pallets: crates and gross per pallet, one pallet
// tare for the line. The SERVER owns the stored crate tare and net per pallet
// (delivery_pallets_snapshot) and the delivery's sums
// (delivery_pallets_sync_delivery); the form's preview below uses the same
// rules so the number the clerk sees is the number that saves.

import { newUuid } from '@/lib/uuid'
import { parseKg } from '@/lib/intakeWeight'

/** One pallet row on the intake form: typed text, parsed at save. */
export interface PalletDraft {
  /** Client-minted pallet_id: the sticker code, and an offline replay's key. */
  id: string
  crates: string
  gross: string
}

/** A saved pallet (delivery_pallets row). */
export interface DeliveryPallet {
  pallet_id:       string
  delivery_id:     string
  addition_id:     string | null
  pallet_no:       number
  crates:          number
  gross_weight_kg: number | null
  pallet_tare_kg:  number
  crate_tare_kg:   number | null
  net_weight_kg:   number | null
}

/** A new pallet row. It copies the previous row's crate count: the pallets of
 *  one load are usually stacked the same way. */
export function newPalletDraft(prev?: PalletDraft): PalletDraft {
  return { id: newUuid(), crates: prev?.crates ?? '', gross: '' }
}

const crateCount = (raw: string): number | null => {
  const t = raw.trim()
  if (!/^\d+$/.test(t)) return null
  const n = parseInt(t, 10)
  return n >= 1 ? n : null
}

/** A pallet's net: gross - crate tare x crates - pallet tare. Null when any
 *  input is missing (no gross, no crate tare). Rounded as the server rounds. */
export function palletNetKg(grossKg: number | null, crates: number, crateTareKg: number | null, palletTareKg: number): number | null {
  if (grossKg == null || grossKg <= 0 || crateTareKg == null || !(crates >= 1)) return null
  const tare = Math.round(crateTareKg * crates * 1000) / 1000
  return Math.round((grossKg - tare - palletTareKg) * 100) / 100
}

export interface PalletLineSums {
  pallets: number
  crates: number
  /** Every pallet weighed: their total gross. Else null (the net is unknown). */
  grossKg: number | null
  weighed: number
  palletTareKg: number
  netKg: number | null
}

/** The line's totals, as the server will store them on the delivery. */
export function palletLineSums(rows: readonly PalletDraft[], palletTareKg: number | null, crateTareKg: number | null): PalletLineSums {
  const tare = palletTareKg != null && palletTareKg > 0 ? palletTareKg : 0
  let crates = 0, gross = 0, weighed = 0, net = 0, netKnown = true
  for (const r of rows) {
    const c = crateCount(r.crates) ?? 0
    const g = parseKg(r.gross)
    crates += c
    if (g != null && g > 0) { gross += g; weighed += 1 }
    const n = palletNetKg(g, c, crateTareKg, tare)
    if (n == null) netKnown = false
    else net += n
  }
  const all = rows.length > 0 && weighed === rows.length
  return {
    pallets: rows.length,
    crates,
    grossKg: all ? Math.round(gross * 100) / 100 : null,
    weighed,
    palletTareKg: Math.round(tare * rows.length * 100) / 100,
    netKg: all && netKnown ? Math.round(net * 100) / 100 : null,
  }
}

/** The form's own refusals for one line's pallets, worded for the clerk. Null = fine. */
export function palletLineProblem(rows: readonly PalletDraft[], palletTareRaw: string, crateTareKg: number | null, label = ''): string | null {
  if (rows.length === 0) return `${label}Add at least one pallet.`
  const tare = parseKg(palletTareRaw)
  if (palletTareRaw.trim() && (tare == null || tare < 0)) return `${label}Pallet tare must be 0 kg or more.`
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]
    const c = crateCount(r.crates)
    if (c == null) return `${label}Pallet ${i + 1}: type how many crates are on it.`
    const g = parseKg(r.gross)
    if (r.gross.trim() && (g == null || g <= 0)) return `${label}Pallet ${i + 1}: the gross weight must be a number above 0 kg.`
    const n = palletNetKg(g, c, crateTareKg, tare ?? 0)
    if (n != null && n < 0) return `${label}Pallet ${i + 1}: the tare is more than its gross weight. Check the weight, the crate type and the crates.`
  }
  return null
}

/** What save_gate_intake_delivery / add_delivery_load_pallets take, in row order. */
export function palletPayload(rows: readonly PalletDraft[], palletTareRaw: string): { pallet_id: string; crates: number; gross_weight_kg: number | null; pallet_tare_kg: number }[] {
  const tare = parseKg(palletTareRaw)
  return rows.map(r => {
    const g = parseKg(r.gross)
    return {
      pallet_id:       r.id,
      crates:          crateCount(r.crates) ?? 0,
      gross_weight_kg: g != null && g > 0 ? g : null,
      pallet_tare_kg:  tare != null && tare > 0 ? tare : 0,
    }
  })
}

/** "Pallet 2 of 3" — the number on the sticker and the tip form. */
export function palletOfText(palletNo: number, count: number): string {
  return `Pallet ${palletNo} of ${count}`
}

/** Tipping's "Use intake weight" for a scanned pallet: its crates and fruit as
 *  weighed at intake (gross less the pallet). Null when it was not weighed. */
export function palletTipGrossKg(p: Pick<DeliveryPallet, 'gross_weight_kg' | 'pallet_tare_kg'>): number | null {
  if (p.gross_weight_kg == null) return null
  const kg = Math.round((Number(p.gross_weight_kg) - Number(p.pallet_tare_kg ?? 0)) * 100) / 100
  return kg > 0 ? kg : null
}

/** A pallet scan at tipping, before the delivery checks (run, add-to-run):
 *  refuse a pallet already tipped, or one whose crates are no longer all left. */
export function palletScanRefusal(p: { pallet_no: number; crates: number; tipped_at: string | null }, count: number, binsRemaining: number): { title: string; message: string } | null {
  const which = palletOfText(p.pallet_no, count)
  if (p.tipped_at) {
    const at = new Date(p.tipped_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })
    return { title: 'Already tipped', message: `${which} was tipped at ${at}. Undo that tip first to tip it again.` }
  }
  if (binsRemaining < p.crates) {
    return {
      title: 'Not enough crates left',
      message: `${which} has ${p.crates} crates, but only ${binsRemaining} of this delivery are left to tip. Some were tipped without a scan: tip the rest by tapping the delivery.`,
    }
  }
  return null
}

/** The board's cached pallets once one is tipped: that pallet carries the tip
 *  time at once. The cache polls every 30 s, so without this a second scan of
 *  the same sticker opened the form again and the server refused the tip
 *  ("Pallet n of this delivery is already tipped."). Offline it is the only
 *  record the board has of the queued tip. Other rows keep their identity. */
export function withPalletTipped<T extends { pallet_id: string; tipped_at: string | null }>(
  rows: readonly T[] | undefined, palletId: string, tippedAt: string,
): T[] | undefined {
  if (!rows) return rows
  return rows.map(p => (p.pallet_id === palletId && !p.tipped_at ? { ...p, tipped_at: tippedAt } : p))
}
